import { lazy, Suspense, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { identifyUser, resetUser, trackPageview } from "../lib/analytics";
import { setMonitoringUser } from "../lib/monitoring";
import { motion, AnimatePresence } from "motion/react";
import { X, Lock, Eye, EyeOff } from "lucide-react";
import { AppNav } from "./AppNav";
import { MobileTabBar } from "./MobileTabBar";
import { OfflineBanner } from "./OfflineBanner";
import { PullToRefresh } from "./PullToRefresh";
import { RitualToast } from "./RitualToast";
import { useAuth } from "../lib/auth/AuthProvider";
import { SignInPage } from "./SignInPage";
// Lazy so the paywall page stays out of the main chunk (routes.ts already
// lazy-loads it; a static import here would fold it back in).
const PricingPage = lazy(() =>
  import("./PricingPage").then((m) => ({ default: m.PricingPage })),
);
import { useSubscription } from "../lib/useSubscription";
import { useScrollManager } from "../lib/useScrollManager";
import { supabase } from "../lib/supabaseClient";
import { BurrowLoader } from "./BurrowLoader";
import { useBurrow } from "./BurrowStore";

function ResetPasswordModal({ onClose }: { onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    setError("");
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) {
      setError(err.message);
    } else {
      onClose();
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-sm rounded-2xl bg-card border border-border/40 shadow-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Lock size={18} className="text-accent" />
              <h2
                className="text-primary"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.1rem" }}
              >
                Set New Password
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
            >
              <X size={16} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label
                className="text-muted-foreground/60 block mb-1.5"
                style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
              >
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 pr-10 rounded-xl border border-border/40 bg-background text-foreground outline-none focus:border-accent/40 transition-colors"
                  style={{ fontSize: "0.88rem" }}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label
                className="text-muted-foreground/60 block mb-1.5"
                style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
              >
                Confirm Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-border/40 bg-background text-foreground outline-none focus:border-accent/40 transition-colors"
                style={{ fontSize: "0.88rem" }}
                placeholder="Confirm new password"
                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              />
            </div>

            {error && (
              <p className="text-destructive" style={{ fontSize: "0.82rem" }}>
                {error}
              </p>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading || !password || !confirm}
              className="w-full py-2.5 rounded-xl bg-accent text-accent-foreground transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ fontSize: "0.88rem", fontFamily: "'Fraunces', Georgia, serif" }}
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
}


// Routes that bypass the paywall
const FREE_ROUTES = ["/pricing", "/settings", "/terms", "/privacy", "/onboarding"];

// Routes that should NOT be blocked by the onboarding gate
// (public/legal routes, public editions accessed by anyone, the gate itself)
const ONBOARDING_EXEMPT = (path: string) =>
  path === "/onboarding" ||
  path === "/terms" ||
  path === "/privacy" ||
  path.startsWith("/editions/") ||
  path.startsWith("/c/");

type OnboardingState = "loading" | "pending" | "complete";

export function RootLayout() {
  const auth = useAuth();
  const sub = useSubscription();
  const { state } = useBurrow();
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll reset/restoration for the #root scroller + iOS keyboard-pan repair.
  useScrollManager();

  // PRD §4.8 — onboarding hard gate.
  // Query profiles.onboarding_completed_at ONCE per signed-in session.
  // The gate-lifting event is fired by OnboardingGate after it writes the
  // timestamp, so we don't need to re-query on every navigation.
  const [onboarding, setOnboarding] = useState<OnboardingState>("loading");
  const userId = auth.status === "signed_in" ? auth.user.id : null;

  useEffect(() => {
    if (!userId) {
      setOnboarding("loading");
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("onboarding_completed_at")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        // Profile row missing (trigger race) — treat as pending so the user
        // lands on the gate rather than the dashboard.
        setOnboarding("pending");
        return;
      }
      setOnboarding(data.onboarding_completed_at ? "complete" : "pending");
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // OnboardingGate dispatches this when it finishes writing the timestamp.
  useEffect(() => {
    const onComplete = () => setOnboarding("complete");
    window.addEventListener("burrow:onboarding-complete", onComplete);
    return () => window.removeEventListener("burrow:onboarding-complete", onComplete);
  }, []);

  // Redirect side effect — only fires when we know the state.
  useEffect(() => {
    if (!userId) return;
    if (onboarding === "loading") return;
    if (onboarding === "pending" && !ONBOARDING_EXEMPT(location.pathname)) {
      navigate("/onboarding", { replace: true });
    } else if (onboarding === "complete" && location.pathname === "/onboarding") {
      navigate("/", { replace: true });
    }
  }, [onboarding, location.pathname, userId, navigate]);

  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);

  // Identify the user in PostHog + Sentry once we know both who they are and
  // (once resolved) which plan they're on, so every event and error is
  // attributable and segmentable by plan/handle.
  useEffect(() => {
    if (auth.status === "signed_in") {
      if (sub.status === "loading") return; // wait so `plan` isn't set then corrected
      const plan = sub.status === "active" || sub.status === "trialing" ? "premium" : "free";
      identifyUser(auth.user, { plan, subscriptionStatus: sub.status });
      setMonitoringUser(auth.user);
    } else if (auth.status === "signed_out") {
      resetUser();
      setMonitoringUser(null);
    }
  }, [auth.status, auth.user, sub.status]);

  if (auth.status === "loading") {
    return <BurrowLoader />;
  }

  if (auth.status === "signed_out") {
    if (
      location.pathname.startsWith("/editions/") ||
      location.pathname.startsWith("/c/") ||
      location.pathname === "/terms" ||
      location.pathname === "/privacy"
    ) {
      return (
        <div className="min-h-dvh bg-background">
          <Outlet />
        </div>
      );
    }
    return <SignInPage />;
  }

  const onFreePage = FREE_ROUTES.some((r) => location.pathname === r) || location.pathname.startsWith("/editions/") || location.pathname.startsWith("/c/");

  // PRD §4.8 — block the dashboard until we've confirmed onboarding status.
  // Don't loader-flash for exempt routes (legal, public editions).
  if (onboarding === "loading" && !ONBOARDING_EXEMPT(location.pathname)) {
    return <BurrowLoader />;
  }
  // Onboarding gate itself bypasses subscription and renders directly.
  if (location.pathname === "/onboarding") {
    return (
      <div className="min-h-dvh bg-background">
        <Outlet />
        <RitualToast />
      </div>
    );
  }

  // Show loader until subscription check completes — never flash pricing
  if (sub.status === "loading" && !onFreePage) {
    return <BurrowLoader />;
  }

  // Paywall: only show after we're certain they're not a subscriber
  const hasAccess = sub.status === "active" || sub.status === "trialing" || sub.status === "loading";

  if (!hasAccess && !onFreePage) {
    return (
      <div className="min-h-dvh bg-background">
        <AppNav />
        <Suspense fallback={<BurrowLoader />}>
          <PricingPage />
        </Suspense>
        <RitualToast />
      </div>
    );
  }

  // Store-backed pages flash false "not found"/empty states (and can error
  // reading half-loaded data) if they render before the initial hydrate. Hold
  // the whole authed app on a loader until the store is ready — a one-time boot
  // wait; `hydrated` stays true afterward, so navigations remain instant. The
  // self-fetching public routes (editions, public collections) do their own
  // loading and don't touch the store, so they're exempt.
  const selfFetchingRoute =
    location.pathname.startsWith("/editions/") || location.pathname.startsWith("/c/");
  if (!state.hydrated && !selfFetchingRoute) {
    return <BurrowLoader />;
  }

  return (
    <div className="min-h-dvh bg-background">
      <OfflineBanner />
      <PullToRefresh />
      <AppNav />
      {/* pb-tab-bar reserves space for the fixed mobile tab bar (0 on desktop) */}
      <div className="pb-tab-bar">
        <Outlet />
      </div>
      <MobileTabBar />
      <RitualToast />
      <AnimatePresence>
        {auth.passwordRecovery && (
          <ResetPasswordModal onClose={auth.clearPasswordRecovery} />
        )}
      </AnimatePresence>
    </div>
  );
}
